import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { sprekersApi } from '../api';
import type { CanvasBoardDetail } from '../types';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  presentationId: string;
  onImported: (board: CanvasBoardDetail) => void;
}

/**
 * Schrijfpad voor Stap 1 (geen visuele editor, Stap 2): een enkele JSON-blob
 * met frames/connections/path, geïmporteerd als geheel (vervangt een
 * bestaand board volledig, zie service.import_canvas). Mirrort
 * SlideFormDialog.tsx's modal-conventies.
 */
export default function CanvasImportDialog({ open, onOpenChange, presentationId, onImported }: Props) {
  const [jsonText, setJsonText] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) setJsonText('');
  }, [open]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    let data: unknown;
    try {
      data = JSON.parse(jsonText);
    } catch (error) {
      toast.error('Ongeldige JSON');
      return;
    }

    setSubmitting(true);
    try {
      const board = await sprekersApi.importCanvas(presentationId, data as Parameters<typeof sprekersApi.importCanvas>[1]);
      onImported(board);
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Importeren mislukt');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>Canvas importeren</DialogTitle>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4">
          <Textarea
            value={jsonText}
            onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setJsonText(e.target.value)}
            rows={16}
            placeholder='{"frames": [...], "connections": [...], "path": [...]}'
            className="font-mono text-xs"
          />
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuleren</Button>
            <Button type="submit" disabled={submitting}>Importeren</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
